/**
 * HelpPage — /help. Ships as a real page with tour replay + FAQ + contact.
 * Previously a "Coming soon" placeholder — the onboarding modal was even
 * pointing users here, creating a dead-end.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Sparkles, Mail, PlayCircle, MessageCircle, BookOpen, Search, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';

const FAQS = [
  { q: 'What is my Konnect trial length and what do I get?',
    a: 'Every new subscriber gets a 7-day free trial with 100 AI credits, full access to every module, and no credit card required. Your trial end date is shown on Settings → Billing. Credits work across image, video, music, eBook, and text generation.' },
  { q: 'How do I invite my team?',
    a: 'Go to Operations → Team Management → Invite Member. For bigger teams, click Bulk Invite and paste a CSV — up to 500 at once. Each invitee gets a magic-link email; if delivery fails, the invite dialog shows a copyable link and a temp password you can share directly.' },
  { q: 'Why is my email verification required to use credits?',
    a: 'To protect your account and stop abuse. Confirming your email unlocks the 100 free credits and every AI feature. Check your inbox for the verification link (sender: hello@konnect.kaharian.com). If it did not arrive, use the "Resend" link in the yellow banner at the top of the app.' },
  { q: 'What is the difference between credits and my subscription?',
    a: 'Your subscription plan gives you access, seats, and monthly credit allocation. Credits are what actually pay for AI generations (image, video, music, text). Settings → Billing manages your plan. Settings → Credits shows your credit balance and lets you top up.' },
  { q: 'How do I sell a generated asset on the Marketplace?',
    a: 'Any item in your Library has a "Sell on Marketplace" button. Set a price → item is listed instantly as a digital download. Buyers pay via Stripe and get a signed one-time download link. See Marketplace → Payouts for your ledger.' },
  { q: 'How do bookings and reminders work?',
    a: 'Bookings are collected on your public /book/:tenantId page. Konnect automatically sends a 24-hour and 2-hour reminder SMS to your client so no-shows drop close to zero. You can disable reminders per tenant in Settings → Booking Policies.' },
  { q: 'Where do I get more credits?',
    a: 'Settings → Credits shows your current balance. You can top up via Stripe, or upgrade your plan to receive a larger monthly credit allocation automatically.' },
];

export default function HelpPage() {
  const [query, setQuery] = useState('');
  const [openIdx, setOpenIdx] = useState(null);

  const filtered = FAQS.filter(f =>
    !query ||
    f.q.toLowerCase().includes(query.toLowerCase()) ||
    f.a.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="help-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <BookOpen className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Help & Support</h1>
          <p className="text-sm text-slate-500">Fast answers, guided tours, and direct access to our team.</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('open-welcome-tour'))}
          className="p-4 text-left rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition"
          data-testid="help-replay-tour"
        >
          <Sparkles className="h-6 w-6 text-emerald-600 mb-2" />
          <div className="font-semibold text-slate-900 text-sm">Watch the tour</div>
          <div className="text-xs text-slate-500 mt-0.5">55-second overview of what Konnect does.</div>
        </button>
        <a
          href="mailto:hello@konnect.kaharian.com"
          className="p-4 text-left rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition"
          data-testid="help-email-support"
        >
          <Mail className="h-6 w-6 text-slate-600 mb-2" />
          <div className="font-semibold text-slate-900 text-sm">Email support</div>
          <div className="text-xs text-slate-500 mt-0.5">hello@konnect.kaharian.com · replies within 24h.</div>
        </a>
        <button
          type="button"
          onClick={() => { try { document.querySelector('[data-testid="sidebar-open-tour"]')?.click(); } catch { /* no-op */ } }}
          className="p-4 text-left rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition"
          data-testid="help-open-chat"
        >
          <MessageCircle className="h-6 w-6 text-slate-600 mb-2" />
          <div className="font-semibold text-slate-900 text-sm">Live chat</div>
          <div className="text-xs text-slate-500 mt-0.5">Coming soon — for urgent issues, email us.</div>
        </button>
      </div>

      {/* FAQ */}
      <Card>
        <CardHeader>
          <CardTitle>Frequently asked</CardTitle>
          <div className="mt-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQ…"
              className="w-full pl-10 pr-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="help-faq-search"
            />
          </div>
        </CardHeader>
        <CardContent className="divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              No FAQ matches &ldquo;{query}&rdquo;. Try a different keyword or <a className="text-emerald-600 hover:underline" href="mailto:hello@konnect.kaharian.com">email us</a>.
            </div>
          ) : filtered.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full py-3 text-left flex items-start gap-2 hover:bg-slate-50 -mx-6 px-6"
              data-testid={`help-faq-${i}`}
            >
              {openIdx === i ? <ChevronDown className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />}
              <div className="flex-1">
                <div className="font-medium text-slate-900 text-sm">{f.q}</div>
                {openIdx === i && <div className="text-sm text-slate-600 mt-2 leading-relaxed">{f.a}</div>}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <div className="text-xs text-slate-500 flex items-center gap-1 pt-2">
        Still stuck? <a href="mailto:hello@konnect.kaharian.com" className="text-emerald-600 hover:underline">hello@konnect.kaharian.com</a> <ExternalLink className="h-3 w-3" />
      </div>
    </div>
  );
}
